import { defineStore } from 'pinia';
import axios from 'axios';
import { decodeJWT } from '@/services/jwt';

export const useAuthStore = defineStore('auth', {
	state: () => ({
		user: null,
		isLoggedIn: false,
		access: 'guest',
		errorMessage: '',
		token: '',
		selector: '',
		is_staff: false,
		super_admin: {
			show_deleted_tasks: false,
			edit_recommendations_fields: false,
			show_market_id: false,
			hide_paused_market: false,
		},
		decodedToken: null,
        loginAnimation: false
	}),
	actions: {
		initializePersistentSettings() {
			const showDeletedTasks = localStorage.getItem('persistent_show_deleted_tasks');
			const showMarketId = localStorage.getItem('persistent_show_market_id');
			const hidePausedMarket = localStorage.getItem('persistent_hide_paused_market');
			
			if (showDeletedTasks !== null) {
				this.super_admin.show_deleted_tasks = showDeletedTasks === 'true';
			}
			if (showMarketId !== null) {
				this.super_admin.show_market_id = showMarketId === 'true';
			}
			if (hidePausedMarket !== null) {
				this.super_admin.hide_paused_market = hidePausedMarket === 'true';
			}
		},

		savePersistentSettings() {
			localStorage.setItem('persistent_show_deleted_tasks', this.super_admin.show_deleted_tasks.toString());
			localStorage.setItem('persistent_show_market_id', this.super_admin.show_market_id.toString());
			localStorage.setItem('persistent_hide_paused_market', this.super_admin.hide_paused_market.toString());
		},

		async signIn(inputs) {
			localStorage.removeItem('subNav');

			try {
				this.errorMessage = '';
				const data = await axios.post('login', inputs, {
					withCredentials: true,
					headers: { 'Content-Type': 'application/json' },
				});
				if (data?.data?.token) {
					this.setDecodedToken(data.data.token);
					this.is_staff = data.data.is_staff || false;
					await this.storeUser();
					this.initializePersistentSettings();
				}
			}
			catch (error) {
				this.errorMessage = 'Incorrect Email or Password';
				setTimeout(() => {
					this.errorMessage = '';
				}, 3000);
				console.error(error);
			}
		},

		async checkAccess(project_id) {
			const response = await axios.post(
				'vuejs/check_access',
				{
					project_id: project_id,
				},
				{ withCredentials: true },
			);

			data = response.data;

			return data;
		},

		async getUser() {
			try {
				const { data } = await axios.get('user', {
					withCredentials: true,
				});
				return data;
			} catch (error) {
				console.error(error);
			}
		},

		async storeUser() {
			try {
				const response = await this.getUser();

				const user = {
					id: response.id,
					name: response.name,
					email: response.email,
					role: response.roles_mask,
					language: response.language,
					loggedInAt: Date.now(),
					// Add is_staff from user endpoint if available
					is_staff: response.is_staff || this.is_staff || false,
				};

				this.user = user;
				this.access = this.getRole();
				// Update is_staff in state if it came from user endpoint
				if (response.is_staff !== undefined) {
					this.is_staff = response.is_staff;
				}
				this.getLogged();
			} catch (error) {
				console.error(error);
			}
		},

		getLogged() {
			// monday
			axios.post(
				'vuejs/monday',
				{
					user_id: this.user.id,
				},
				{ withCredentials: true },
			);

			this.isLoggedIn = true;
		},

		getRole() {
			if (this.user) {
				switch (this.user.role) {
					case 262144:
						return 'super_admin';
					case 1:
						return 'admin';
					case 8192:
						return 'project manager';
					case 32768:
						return 'digital specialist';
					case 65536:
						return 'digital enabler';
					case 2048:
						return 'partner';
					default:
						return 'guest';
				}
			}
			return null;
		},

		hasAccess(neededRole) {
			let allowedRoles = [];

			switch (this.access) {
				case 'super_admin':
					return true;

				case 'admin':
					if (neededRole !== 'super_admin') {
						return true;
					} else {
						return false;
					}

				case 'project manager':
					allowedRoles = ['super_admin', 'admin'];
					if (!allowedRoles.includes(neededRole)) {
						return true;
					} else {
						return false;
					}

				case 'digital specialist':
					allowedRoles = ['super_admin', 'admin', 'project manager'];
					if (!allowedRoles.includes(neededRole)) {
						return true;
					} else {
						return false;
					}

				case 'digital enabler':
					allowedRoles = ['super_admin', 'admin', 'digital specialist', 'project manager'];
					if (!allowedRoles.includes(neededRole)) {
						return true;
					} else {
						return false;
					}

				case 'partner':
					allowedRoles = ['super_admin', 'admin', 'digital specialist', 'project manager'];
					if (!allowedRoles.includes(neededRole)) {
						return true;
					} else {
						return false;
					}

				default:
					return false;
			}
		},

		// Add helper method to check if user is staff
		isStaff() {
			return this.is_staff === true;
		},

		hasUser() {
			return this.user != null;
		},

		logout() {
			this.user = null;
			this.isLoggedIn = false;
			this.access = 'guest';
			this.is_staff = false;
			this.loginAnimation = false;
			this.token = '';
			this.selector = '';
			this.decodedToken = null;
			this.errorMessage = '';
			
			this.super_admin = {
				show_deleted_tasks: false,
				edit_recommendations_fields: false,
				show_market_id: false,
				hide_paused_market: false,
			};
			
			this.initializePersistentSettings();
		},

		setTokenAndSelector(token, selector) {
			this.token = token;
			this.selector = selector;
		},

		changeLanguage(language) {
			this.user.language = language;
		},

		async createPassword(inputs) {
			let data = {};

			const response = await axios
				.post(
					'create-password/',
					{
						...inputs,
						// token: token,
						// selector: this.selector
					},
					{ withCredentials: true },
				)
				.then(function (response) {
					data = {
						success: response?.data?.success,
						message: response?.data?.message,
					};
				})
				.catch(function (error) {
					if (error?.response?.status !== 200) {
						data = {
							success: error?.response?.data?.success,
							message: error?.response?.data?.message,
						};
					}
				});

			return data;
		},

		async verifyTokenAndSelector(token, selector) {
			try {
				const response = await axios.post(
					'validate-user-token',
					{
						token: token,
						selector: selector,
					},
					{
						withCredentials: true,
						headers: { 'Content-Type': 'application/json' },
					},
				);

				if (response?.status === 200) {
					this.setTokenAndSelector(token, selector);
				}
			} catch (error) {
				console.error(error);
			}
		},

		updateRole(role) {
			if (this.user && this.user.role !== role) {
				this.user.role = role;
				this.access = this.getRole();
				location.reload();
			}
		},

		setDecodedToken(token) {
			this.decodedToken = decodeJWT(token);
			this.decodedToken.token = token;
		},

		setToken(token) {
			this.token = token;
			this.decodedToken = decodeJWT(token);
		},

		async googleLoginApi(inputs) {
			try {
				const data = await axios.post('google-oauth2', inputs, {
					withCredentials: true,
					headers: { 'Content-Type': 'application/json' },
				});

				if (data) {
					this.setDecodedToken(data.data.token);
					this.is_staff = data.data.is_staff || false;
					await this.storeUser();
					this.initializePersistentSettings();
				}
			} catch (error) {
				console.error(error);
			}
		},

		async microsoftLoginApi(inputs) {
			try {
				const data = await axios.post('microsoft-oauth2', inputs, {
					withCredentials: true,
					headers: { 'Content-Type': 'application/json' },
				});

				if (data) {
					this.setDecodedToken(data.data.token);
					this.is_staff = data.data.is_staff || false;
					await this.storeUser();
					this.initializePersistentSettings();
				}
			} catch (error) {
				console.error(error);
			}
		},
	},
	persist: true,
});